import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import PromoFeature from '~/components/Promo/PromoFeature';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import usePromoFeatureAvailability from '~/hooks/usePromoFeatureAvailability';

import { getMinSpendProductTypeOptions, getProductTypeOptions } from '~/services/PromoService';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import { parseVoucherRewardCSV, toCSV } from './voucherRewardCsv';

type VoucherReward = definitions['VoucherRewardV1'];

type Props = {
  rewards: Array<VoucherReward>;
  onChange: (rewards: Array<VoucherReward>) => void;
  disabled?: boolean;
};

export default function VoucherRewardCsvImportExport({ rewards, onChange, disabled = false }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const canImportExportRewards = usePromoFeatureAvailability('voucher-reward-csv-import-export');
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();
  const promoProductTypes = promoMeta?.promo_product_types ?? [];

  const [importText, setImportText] = useState('');
  const [isImportPreviewOpen, setIsImportPreviewOpen] = useState(false);
  const [allowImportCreate, setAllowImportCreate] = useState(true);
  const [allowImportUpdate, setAllowImportUpdate] = useState(true);

  // Derived from the live table rows so successful table edits immediately refresh the export text.
  const exportText = useMemo(() => toCSV(rewards), [rewards]);
  const importResult = useMemo(() => {
    if (!importText.trim()) {
      return null;
    }
    return parseVoucherRewardCSV(importText, {
      existingRewards: rewards,
      validCurrencies: getAllCurrencyCodes(),
      validProducts: getProductTypeOptions(promoProductTypes, []).map((product) => product.value),
      validMinSpendProducts: getMinSpendProductTypeOptions(promoProductTypes, []).map((product) => product.value),
      allowCreate: allowImportCreate,
      allowUpdate: allowImportUpdate,
    });
  }, [allowImportCreate, allowImportUpdate, importText, promoProductTypes, rewards]);

  const importProblemRows =
    importResult?.candidates.filter((candidate) => candidate.warnings.some((warning) => warning.level === 'error')) ??
    [];
  const importCandidateWarnings = importResult?.candidates.filter((candidate) => candidate.warnings.length > 0) ?? [];
  const importWarnings = importResult?.warnings ?? [];
  const validImportCandidates =
    importResult?.candidates.filter((candidate) => candidate.warnings.every((warning) => warning.level !== 'error')) ??
    [];
  const createCount = validImportCandidates.filter((candidate) => candidate.action === 'create').length;
  const updateCount = validImportCandidates.filter((candidate) => candidate.action === 'update').length;
  const importPreviewRows = validImportCandidates.map((candidate) => ({
    rowNumber: candidate.rowNumber,
    action: candidate.action,
    reward: candidate.reward,
  }));

  const handleImportRewards = useCallback(() => {
    if (!importResult || importWarnings.length > 0 || importProblemRows.length > 0) {
      return;
    }

    const importedRewards = validImportCandidates.map((candidate) => candidate.reward);
    const importedUpdates = new Map(
      validImportCandidates
        .filter((candidate) => candidate.action === 'update')
        .map((candidate) => [candidate.reward.id_voucher_reward, candidate.reward]),
    );
    onChange([
      ...rewards.map((reward) => importedUpdates.get(reward.id_voucher_reward) ?? reward),
      ...validImportCandidates
        .filter((candidate) => candidate.action === 'create')
        .map((candidate) => candidate.reward),
    ]);
    setImportText('');
    setIsImportPreviewOpen(false);
    enqueueSnackbar(`Imported ${importedRewards.length} voucher reward rows`, { variant: 'success' });
  }, [
    enqueueSnackbar,
    importProblemRows.length,
    importResult,
    importWarnings.length,
    onChange,
    rewards,
    validImportCandidates,
  ]);

  const handleOpenImportPreview = useCallback(() => {
    if (!importResult || importWarnings.length > 0 || importProblemRows.length > 0) {
      return;
    }

    setIsImportPreviewOpen(true);
  }, [importProblemRows.length, importResult, importWarnings.length]);

  const handleCloseImportPreview = useCallback(() => {
    setIsImportPreviewOpen(false);
  }, []);

  if (!canImportExportRewards) {
    return null;
  }

  return (
    <Box mt={2}>
      <PromoFeature
        featureKey="voucher-reward-csv-import-export"
        presentation="accordion"
        fallbackTitle="Voucher reward CSV import and export"
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Export reflects all reward rows currently shown above. A row ID already on this voucher updates that reward;
            a blank or unmatched ID creates a new reward, so exports can be imported into another voucher.
          </Typography>
          <TextField
            label="Export voucher reward CSV"
            value={exportText}
            multiline
            minRows={5}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="Import voucher reward CSV"
            value={importText}
            onChange={(event) => setImportText(event.target.value)}
            multiline
            minRows={6}
            fullWidth
            disabled={disabled || isPromoMetaLoading}
            helperText="Paste CSV exported above. Every row must be valid before import is enabled."
          />
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowImportCreate}
                  onChange={(event) => setAllowImportCreate(event.target.checked)}
                  disabled={disabled || isPromoMetaLoading}
                />
              }
              label="Allow Create"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowImportUpdate}
                  onChange={(event) => setAllowImportUpdate(event.target.checked)}
                  disabled={disabled || isPromoMetaLoading}
                />
              }
              label="Allow Update"
            />
          </Stack>
          {importResult && (
            <Stack spacing={1}>
              <Typography variant="body2">
                {createCount} record{createCount === 1 ? '' : 's'} valid to create, {updateCount} record
                {updateCount === 1 ? '' : 's'} valid to update, {importProblemRows.length} row
                {importProblemRows.length === 1 ? '' : 's'} with problems.
              </Typography>
              {importWarnings.map((warning, index) => (
                <Alert key={`${warning.message}-${index}`} severity="error">
                  {warning.message}
                </Alert>
              ))}
              {importCandidateWarnings.map((candidate) => (
                <Alert
                  key={candidate.rowNumber}
                  severity={candidate.warnings.some((warning) => warning.level === 'error') ? 'error' : 'warning'}
                >
                  Row {candidate.rowNumber}: {candidate.warnings.map((warning) => warning.message).join('; ')}
                </Alert>
              ))}
            </Stack>
          )}
          <Box>
            <Button
              variant="contained"
              onClick={handleOpenImportPreview}
              disabled={
                disabled ||
                isPromoMetaLoading ||
                !importResult ||
                validImportCandidates.length === 0 ||
                importWarnings.length > 0 ||
                importProblemRows.length > 0
              }
            >
              Import reward rows
            </Button>
          </Box>
        </Stack>
        <Dialog open={isImportPreviewOpen} onClose={handleCloseImportPreview} fullWidth maxWidth="md">
          <DialogTitle>Review import changes</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Alert severity="info">
                This import will create {createCount} row{createCount === 1 ? '' : 's'} and update {updateCount} row
                {updateCount === 1 ? '' : 's'}.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Review the rows below before applying the import. Only rows with no errors will be written.
              </Typography>
              <Stack spacing={1}>
                {importPreviewRows.map((row) => (
                  <Box key={`${row.action}-${row.rowNumber}`} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2">
                      Row {row.rowNumber}: {row.action === 'create' ? 'Create' : 'Update'} reward
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {row.reward.id_voucher_reward}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Amount: {row.reward.reward_amount} {row.reward.currency_code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Products: {row.reward.products.join(', ') || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Min spend products: {row.reward.min_spend_products?.join(', ') || '-'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={handleCloseImportPreview}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleImportRewards}>
              Apply import
            </Button>
          </DialogActions>
        </Dialog>
      </PromoFeature>
    </Box>
  );
}
